import {
	Area,
	AreaChart,
	CartesianGrid,
	Legend,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

interface TimeSeriesPoint {
	timestamp: string;
	cacheHits: number;
	hotCacheHits: number;
	semanticCacheHits: number;
	requests: number;
}

interface CacheHitChartProps {
	data: TimeSeriesPoint[];
	isLoading?: boolean;
}

export function CacheHitChart({ data, isLoading }: CacheHitChartProps) {
	if (isLoading) {
		return (
			<Card>
				<CardHeader>
					<CardTitle className="text-lg">Cache Performance</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="h-64 flex items-center justify-center">
						<div className="text-sm text-muted-foreground">
							Loading chart data...
						</div>
					</div>
				</CardContent>
			</Card>
		);
	}

	if (!data || data.length === 0) {
		return (
			<Card>
				<CardHeader>
					<CardTitle className="text-lg">Cache Performance</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="h-64 flex items-center justify-center">
						<div className="text-sm text-muted-foreground">
							No data available
						</div>
					</div>
				</CardContent>
			</Card>
		);
	}

	const formattedData = data.map((point) => ({
		...point,
		cacheHitRate:
			point.requests > 0
				? ((point.cacheHits / point.requests) * 100).toFixed(1)
				: 0,
		label: point.timestamp.includes("T")
			? `${new Date(point.timestamp).toLocaleDateString("en-US", { month: "short", day: "numeric" })} ${new Date(point.timestamp).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}`
			: new Date(point.timestamp).toLocaleDateString("en-US", {
					month: "short",
					day: "numeric",
				}),
	}));

	return (
		<Card>
			<CardHeader>
				<CardTitle className="text-lg">Cache Performance</CardTitle>
			</CardHeader>
			<CardContent>
				<ResponsiveContainer width="100%" height={300}>
					<AreaChart
						data={formattedData}
						margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
					>
						<CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
						<XAxis
							dataKey="label"
							className="text-xs"
							tick={{ fill: "currentColor" }}
							angle={-45}
							textAnchor="end"
							height={60}
						/>
						<YAxis className="text-xs" tick={{ fill: "currentColor" }} />
						<Tooltip
							contentStyle={{
								backgroundColor: "hsl(var(--card))",
								border: "1px solid hsl(var(--border))",
								borderRadius: "6px",
							}}
							labelStyle={{ color: "hsl(var(--foreground))" }}
						/>
						<Legend />
						<Area
							type="monotone"
							dataKey="hotCacheHits"
							stackId="1"
							stroke="hsl(142, 76%, 36%)"
							fill="hsl(142, 76%, 36%)"
							fillOpacity={0.6}
							name="Hot Cache"
						/>
						<Area
							type="monotone"
							dataKey="semanticCacheHits"
							stackId="1"
							stroke="hsl(221, 83%, 53%)"
							fill="hsl(221, 83%, 53%)"
							fillOpacity={0.6}
							name="Semantic Cache"
						/>
					</AreaChart>
				</ResponsiveContainer>
			</CardContent>
		</Card>
	);
}
